import { useState, useEffect, type FormEvent } from 'react';
import type { TimetableSlot } from './timetableMockData';
import { DAYS, PERIODS } from './timetableMockData';
import { fetchStaffList, mapStaffToUi, type MappedStaff } from '../../services/staffApi';
import { fetchClasses as fetchClassList, fetchClassSections, fetchClassSubjects } from '../../services/classApi';
import type { ClassApiRecord, SectionApiRecord, ClassSubjectRecord } from '../../services/classApi';
import { fetchAcademicSessions, type AcademicSessionRecord } from '../../services/examApi';
import { fetchTimetableSlots, saveTimetableSlot } from '../../services/timetableApi';
import type { TimetableApiRecord } from '../../services/timetableApi';
import type { ApiError } from '../../services/api';
import './TimetableBuilder.css';
import { useTranslation } from 'react-i18next';

function dayNameToNumber(name: string): number {
    const map: Record<string, number> = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 7 };
    return map[name] ?? 0;
}

function mapApiToSlot(r: TimetableApiRecord): TimetableSlot {
    const teacherName = r.teacher
        ? [r.teacher.first_name, r.teacher.last_name].filter(Boolean).join(' ')
        : null;
    return {
        id: r.id,
        day_of_week: dayNameToNumber(r.day_of_week),
        period: r.period_number,
        subject_id: r.subject_id,
        subject_name: r.subject?.subject_name ?? null,
        teacher_id: r.teacher_id,
        teacher_name: teacherName,
        room_number: r.room_number,
        start_time: r.start_time?.slice(0, 5) ?? '',
        end_time: r.end_time?.slice(0, 5) ?? '',
        has_conflict: false,
        conflict_message: null,
    };
}

export default function TimetableBuilder() {
    const { t: _t } = useTranslation();
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedSection, setSelectedSection] = useState('');
    const [slots, setSlots] = useState<TimetableSlot[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [saveError, setSaveError] = useState('');
    const [dirtySlotIds, setDirtySlotIds] = useState<Set<string>>(new Set());

    /* ---- API data ---- */
    const [classes, setClasses] = useState<ClassApiRecord[]>([]);
    const [sections, setSections] = useState<SectionApiRecord[]>([]);
    const [subjects, setSubjects] = useState<ClassSubjectRecord[]>([]);
    const [teachers, setTeachers] = useState<MappedStaff[]>([]);
    const [sessions, setSessions] = useState<AcademicSessionRecord[]>([]);
    const [selSession, setSelSession] = useState('');
    const [classesLoading, setClassesLoading] = useState(true);
    const [sectionsLoading, setSectionsLoading] = useState(false);

    /* ---- Drawer state ---- */
    const [drawerSlot, setDrawerSlot] = useState<{ day: number; period: number } | null>(null);
    const [formSubject, setFormSubject] = useState('');
    const [formTeacher, setFormTeacher] = useState('');
    const [formRoom, setFormRoom] = useState('');
    const [formStartTime, setFormStartTime] = useState('');
    const [formEndTime, setFormEndTime] = useState('');

    /* ---- Tooltip ---- */
    const [hoveredConflict, setHoveredConflict] = useState<string | null>(null);

    const hasContext = !!selectedClass;

    /* ---- Fetch classes & sessions on mount ---- */
    useEffect(() => {
        (async () => {
            try {
                setClassesLoading(true);
                const [classRes, sessionData, staffRes] = await Promise.all([
                    fetchClassList({ perPage: 100 }),
                    fetchAcademicSessions(),
                    fetchStaffList({ perPage: 200, isTeacher: true }),
                ]);
                setClasses(classRes.data);
                setSessions(sessionData);
                setTeachers(staffRes.data.map(mapStaffToUi));
                const current = sessionData.find((s) => s.is_current);
                if (current) setSelSession(current.id);
            } catch {
                setClasses([]);
                setSessions([]);
            } finally {
                setClassesLoading(false);
            }
        })();
    }, []);

    /* ---- Fetch sections when class changes ---- */
    useEffect(() => {
        setSections([]);
        setSelectedSection('');
        if (!selectedClass) return;
        (async () => {
            try {
                setSectionsLoading(true);
                const res = await fetchClassSections(selectedClass, { perPage: 100 });
                setSections(res.data);
            } catch {
                setSections([]);
            } finally {
                setSectionsLoading(false);
            }
        })();
    }, [selectedClass]);

    /* ---- Load timetable ---- */
    useEffect(() => {
        if (!hasContext) { setSlots([]); return; }
        (async () => {
            try {
                setLoading(true);
                setDirtySlotIds(new Set());
                const records = await fetchTimetableSlots({
                    academic_session_id: selSession || undefined,
                    class_id: selectedClass,
                    section_id: selectedSection || null,
                    per_page: 100,
                });
                setSlots(records.map(mapApiToSlot));
            } catch {
                setSlots([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [selectedClass, selectedSection, selSession, hasContext]);

    /* ---- Fetch subjects for class/section ---- */
    useEffect(() => {
        setSubjects([]);
        if (!hasContext) return;
        (async () => {
            try {
                const res = await fetchClassSubjects(selectedClass, { sectionId: selectedSection || undefined, perPage: 100 });
                setSubjects(res.data);
            } catch {
                setSubjects([]);
            }
        })();
    }, [selectedClass, selectedSection, hasContext]);

    const handleClassChange = (id: string) => {
        setSelectedClass(id);
        setSelectedSection('');
        setDrawerSlot(null);
    };

    /* ---- Get slot for a day/period ---- */
    const getSlot = (day: number, period: number): TimetableSlot | undefined =>
        slots.find((s) => s.day_of_week === day && s.period === period);

    /* ---- Open drawer for a cell ---- */
    const openSlotDrawer = (day: number, period: number) => {
        const existing = getSlot(day, period);
        const periodDef = PERIODS.find((p) => p.period === period);
        setDrawerSlot({ day, period });
        setFormSubject(existing?.subject_id || '');
        setFormTeacher(existing?.teacher_id || '');
        setFormRoom(existing?.room_number || '');
        setFormStartTime(existing?.start_time || periodDef?.start_time || '');
        setFormEndTime(existing?.end_time || periodDef?.end_time || '');
    };

    const closeDrawer = () => { setDrawerSlot(null); };

    /* ---- Save slot locally ---- */
    const handleSlotSubmit = (ev: FormEvent) => {
        ev.preventDefault();
        if (!drawerSlot) return;
        const { day, period } = drawerSlot;
        const slotId = `slot-${day}-${period}`;
        const subj = subjects.find((s) => s.id === formSubject);
        const teacher = teachers.find((t) => t.user_id === formTeacher);

        const newSlot: TimetableSlot = {
            id: slotId,
            day_of_week: day,
            period,
            subject_id: formSubject || null,
            subject_name: subj?.subject_name || null,
            teacher_id: formTeacher || null,
            teacher_name: teacher ? [teacher.first_name, teacher.last_name].filter(Boolean).join(' ') : null,
            room_number: formRoom || null,
            start_time: formStartTime,
            end_time: formEndTime,
            has_conflict: false,
            conflict_message: null,
        };

        setSlots((prev) => {
            const withoutCurrent = prev.filter((s) => !(s.day_of_week === day && s.period === period));
            return formSubject ? [...withoutCurrent, newSlot] : withoutCurrent;
        });

        setDirtySlotIds((prev) => new Set(prev).add(slotId));
        closeDrawer();
    };

    /* ---- Clear a slot ---- */
    const clearSlot = () => {
        if (!drawerSlot) return;
        const { day, period } = drawerSlot;
        setSlots((prev) => prev.filter((s) => !(s.day_of_week === day && s.period === period)));
        setDirtySlotIds((prev) => new Set(prev).add(`slot-${day}-${period}`));
        closeDrawer();
    };

    /* ---- Batch save ---- */
    const handleBatchSave = async () => {
        if (!selSession) {
            setSaveError('Please select an academic session before saving.');
            setTimeout(() => setSaveError(''), 5000);
            return;
        }

        const changed = slots.filter((s) => dirtySlotIds.has(s.id));
        if (changed.length === 0) return;

        setSaving(true);
        setSaveError('');
        try {
            await Promise.all(changed.map((slot) => {
                const dayName = DAYS.find((d) => d.value === slot.day_of_week)?.label;
                if (!dayName || !slot.subject_id || !slot.teacher_id) return Promise.resolve();
                return saveTimetableSlot({
                    academic_session_id: selSession,
                    class_id: selectedClass,
                    section_id: selectedSection || null,
                    class_section_subject_id: slot.subject_id,
                    teacher_id: slot.teacher_id,
                    day_of_week: dayName,
                    period_number: slot.period,
                    start_time: slot.start_time,
                    end_time: slot.end_time,
                    room_number: slot.room_number || null,
                });
            }));
            setDirtySlotIds(new Set());
            setSuccessMsg('Timetable saved successfully!');
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            setSaveError(apiErr.message || 'Failed to save timetable.');
            setTimeout(() => setSaveError(''), 5000);
        } finally {
            setSaving(false);
        }
    };

    const dayLabel = drawerSlot ? DAYS.find((d) => d.value === drawerSlot.day)?.label : '';
    const periodLabel = drawerSlot ? `Period ${drawerSlot.period}` : '';

    return (
        <div className="tt">
            {successMsg && (
                <div className="tt__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="tt__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}
            {saveError && (
                <div className="tt__toast tt__toast--error">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                    <span>{saveError}</span>
                    <button className="tt__toast-dismiss" onClick={() => setSaveError('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            <div className="tt__header">
                <div className="tt__header-text">
                    <h1 className="tt__title">Timetable Builder</h1>
                    <p className="tt__subtitle">Design and manage class timetables.</p>
                </div>
                <div className="tt__header-actions">
                    {dirtySlotIds.size > 0 && (
                        <span className="tt__unsaved-badge">{dirtySlotIds.size} unsaved</span>
                    )}
                    {hasContext && (
                        <button className="tt__save-btn" disabled={dirtySlotIds.size === 0 || saving} onClick={handleBatchSave}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
                            {saving ? 'Saving…' : 'Save Timetable'}
                        </button>
                    )}
                </div>
            </div>

            {/* Context bar */}
            <div className="tt__context">
                <span className="tt__context-label">Session</span>
                <select className="tt__context-select" value={selSession} onChange={(e) => setSelSession(e.target.value)} disabled={classesLoading}>
                    <option value="">{classesLoading ? 'Loading…' : 'Select Session'}</option>
                    {sessions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <div className="tt__context-divider" />
                <span className="tt__context-label">Class</span>
                <select className="tt__context-select" value={selectedClass} onChange={(e) => handleClassChange(e.target.value)} disabled={classesLoading}>
                    <option value="">{classesLoading ? 'Loading…' : 'Select Class'}</option>
                    {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div className="tt__context-divider" />
                <span className="tt__context-label">Section</span>
                <select className="tt__context-select" value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)} disabled={!selectedClass || sectionsLoading}>
                    <option value="">{sectionsLoading ? 'Loading…' : 'Select Section'}</option>
                    {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
            </div>

            {/* Content */}
            {!hasContext ? (
                <div className="tt__placeholder">
                    <div className="tt__placeholder-icon">
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                    </div>
                    <h3 className="tt__placeholder-title">Select a Class</h3>
                    <p className="tt__placeholder-text">Choose a class (and optionally a section) above to build the timetable.</p>
                </div>
            ) : loading ? (
                <div className="tt__loading"><div className="tt__loader"><span className="tt__loader-dot" /><span className="tt__loader-dot" /><span className="tt__loader-dot" /></div></div>
            ) : (
                <div className="tt__matrix-wrap">
                    <div
                        className="tt__matrix"
                        style={{ gridTemplateColumns: `90px repeat(${PERIODS.length}, 1fr)` }}
                    >
                        {/* Corner cell */}
                        <div className="tt__corner">Day / Period</div>

                        {/* Period headers */}
                        {PERIODS.map((p) => (
                            <div className="tt__period-header" key={p.period}>
                                <span className="tt__period-number">P{p.period}</span>
                                <span className="tt__period-time">{p.start_time} – {p.end_time}</span>
                            </div>
                        ))}

                        {/* Day rows */}
                        {DAYS.map((day) => (
                            <>
                                {/* Day label */}
                                <div className="tt__day-header" key={`day-${day.value}`}>{day.short}</div>

                                {/* Slot cells for this day */}
                                {PERIODS.map((period) => {
                                    const slot = getSlot(day.value, period.period);
                                    const isEmpty = !slot;
                                    const slotKey = `${day.value}-${period.period}`;

                                    return (
                                        <div
                                            key={slotKey}
                                            className={`tt__slot ${isEmpty ? 'tt__slot--empty' : 'tt__slot--filled'} ${slot?.has_conflict ? 'tt__slot--conflict' : ''}`}
                                            onClick={() => { if (!drawerSlot) openSlotDrawer(day.value, period.period); }}
                                            onMouseEnter={() => slot?.has_conflict ? setHoveredConflict(slotKey) : null}
                                            onMouseLeave={() => setHoveredConflict(null)}
                                        >
                                            {isEmpty ? (
                                                <span className="tt__slot-add">
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                                                </span>
                                            ) : (
                                                <>
                                                    <span className="tt__slot-subject">{slot.subject_name}</span>
                                                    <span className="tt__slot-teacher">{slot.teacher_name || 'No teacher'}</span>
                                                    {slot.room_number && <span className="tt__slot-room">{slot.room_number}</span>}
                                                    {slot.has_conflict && (
                                                        <span className="tt__slot-conflict-icon">
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                                                        </span>
                                                    )}
                                                    {slot.has_conflict && hoveredConflict === slotKey && (
                                                        <div className="tt__tooltip">{slot.conflict_message}</div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        ))}
                    </div>
                </div>
            )}

            {/* ---- Slot Drawer ---- */}
            {drawerSlot && (
                <div className="tt__overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) closeDrawer(); }}>
                    <aside className="tt__drawer" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="tt__drawer-header">
                            <h2 className="tt__drawer-title">Edit Slot</h2>
                            <button className="tt__drawer-close" onClick={closeDrawer} title="Close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
                        </div>
                        <form className="tt__drawer-body" onSubmit={handleSlotSubmit} id="slot-form">
                            {/* Read-only context */}
                            <div className="tt__card">
                                <h3 className="tt__card-title">Slot Details</h3>
                                <div className="tt__info-row">
                                    <div className="tt__info-item"><span className="tt__info-label">Day</span><span className="tt__info-value">{dayLabel}</span></div>
                                    <div className="tt__info-item"><span className="tt__info-label">Period</span><span className="tt__info-value">{periodLabel}</span></div>
                                </div>
                            </div>

                            {/* Subject & Teacher */}
                            <div className="tt__card">
                                <h3 className="tt__card-title">Assignment</h3>
                                <div className="tt__field">
                                    <label className="tt__label" htmlFor="slotSubject">Subject</label>
                                    <select className="tt__select" id="slotSubject" value={formSubject} onChange={(e) => setFormSubject(e.target.value)}>
                                        <option value="">— None —</option>
                                        {subjects.map((s) => <option key={s.id} value={s.id}>{s.subject_name} ({s.subject_code})</option>)}
                                    </select>
                                </div>
                                <div className="tt__field">
                                    <label className="tt__label" htmlFor="slotTeacher">Teacher</label>
                                    <select className="tt__select" id="slotTeacher" value={formTeacher} onChange={(e) => setFormTeacher(e.target.value)}>
                                        <option value="">— Unassigned —</option>
                                        {teachers.map((t) => <option key={t.user_id} value={t.user_id}>{[t.first_name, t.last_name].filter(Boolean).join(' ')}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Time & Room */}
                            <div className="tt__card">
                                <h3 className="tt__card-title">Schedule</h3>
                                <div className="tt__form-row">
                                    <div className="tt__field">
                                        <label className="tt__label" htmlFor="slotStart">Start Time</label>
                                        <input className="tt__input" type="time" id="slotStart" value={formStartTime} onChange={(e) => setFormStartTime(e.target.value)} />
                                    </div>
                                    <div className="tt__field">
                                        <label className="tt__label" htmlFor="slotEnd">End Time</label>
                                        <input className="tt__input" type="time" id="slotEnd" value={formEndTime} onChange={(e) => setFormEndTime(e.target.value)} />
                                    </div>
                                </div>
                                <div className="tt__field">
                                    <label className="tt__label" htmlFor="slotRoom">Room Number</label>
                                    <input className="tt__input" type="text" id="slotRoom" placeholder="e.g. 101, Lab-1" value={formRoom} onChange={(e) => setFormRoom(e.target.value)} />
                                </div>
                            </div>
                        </form>

                        <div className="tt__drawer-footer">
                            <button className="tt__submit" type="submit" form="slot-form">Apply</button>
                            <button className="tt__clear-btn" type="button" onClick={clearSlot}>Clear Slot</button>
                            <button className="tt__cancel" type="button" onClick={closeDrawer}>Cancel</button>
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
}
